import { useState, useRef, useEffect } from 'react'
import { config } from '@/config/env'
import {
  listRecordings,
  deleteRecording,
  type Recording,
  type RecordingStatus,
} from '@/features/meeting/services/recordings.api'
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { motion } from 'framer-motion'
import { Play, Download, Search, Clock, ArrowLeft, Share2, ChevronDown, Calendar, Send, Mail, Link2, Copy, Users, Lock, Globe, Eye, Settings, CheckCircle, Trash2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/clientWeb/components/ui/button'
import { Badge } from '@/clientWeb/components/ui/badge'
import { EmptyState } from '@/clientWeb/components/ui/empty-state'
import { toast } from '@/clientWeb/components/ui/toast-utils'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/clientWeb/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/clientWeb/components/ui/select'
import { Switch } from '@/clientWeb/components/ui/switch'
import assetNoRegistrantsCopy from '@/assets/noRegistrants copy.svg'
import assetWhatsapp from '@/assets/whatsapp.svg'
import { absolutizeRecordingUrl } from '@/features/meeting/services/recordings.api'

/**
 * A recording as this page renders it.
 *
 * Kept in the shape the existing cards already consume so the visual layer is
 * untouched — only where the data comes from changes.
 */
interface RecordingCard {
  id: string
  title: string
  recordedAt: string
  duration: string
  size: string
  participants: number
  thumb: string
  type: 'meeting' | 'webinar'
  host: { name: string; avatar: string }
  playbackUrl: string | null
  status: RecordingStatus
}

function formatDuration(seconds: number | null): string {
  if (!seconds) return '0:00'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const sec = seconds % 60
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    : `${m}:${String(sec).padStart(2, '0')}`
}

function formatSize(bytes: number | null): string {
  if (!bytes) return '—'
  const mb = bytes / 1_048_576
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${Math.round(mb)} MB`
}

function toCard(r: Recording): RecordingCard {
  const when = r.startedAt ? new Date(r.startedAt) : new Date(r.createdAt)
  return {
    id: r.id,
    // The mixer records a room, not a titled document. Until the backend joins
    // the meeting title through, the date is the honest label — better than
    // inventing one that will not match what the user remembers.
    title: `Recording — ${when.toLocaleDateString()}`,
    recordedAt: `${when.toLocaleDateString()} • ${when.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
    duration: formatDuration(r.durationSeconds),
    size: formatSize(r.bytes),
    participants: 0,
    // Empty when the mixer produced no still — generation is best-effort, and
    // the card renders a play-icon placeholder rather than a broken image.
    thumb: absolutizeRecordingUrl(r.thumbnailUrl) ?? '',
    type: 'meeting',
    host: { name: '', avatar: '' },
    playbackUrl: absolutizeRecordingUrl(r.playbackUrl),
    status: r.status,
  }
}

export default function RecordingsPage() {
  const t = useTranslation()
  const navigate = useNavigate()

  const SHARE_PLATFORMS = [
    { id: 'whatsapp', name: t('analytics.whatsapp'), icon: assetWhatsapp, color: 'bg-green-500' },
    { id: 'telegram', name: t('analytics.telegram'), icon: Send, color: 'bg-blue-500' },
    { id: 'email', name: t('analytics.email'), icon: Mail, color: 'bg-gray-500' },
    { id: 'copy', name: t('analytics.copyLink'), icon: Link2, color: 'bg-purple-500' },
  ]

  const PERMISSION_OPTIONS = [
    {
      id: 'no_share',
      title: t('analytics.dontShareWithAnyone'),
      description: t('analytics.dontShareDesc'),
      icon: Lock
    },
    {
      id: 'anyone_org',
      title: t('analytics.anyoneInOrganization'),
      description: t('analytics.anyoneInOrgDesc'),
      icon: Users
    },
    {
      id: 'anyone_link',
      title: t('analytics.anyoneWithLink'),
      description: t('analytics.anyoneWithLinkDesc'),
      icon: Globe
    },
    {
      id: 'participants_org',
      title: t('analytics.participantsInOrganization'),
      description: t('analytics.participantsInOrgDesc'),
      icon: CheckCircle
    }
  ]

  const [search, setSearch] = useState('')
  const [view, setView] = useState<'grid' | 'list'>('grid')
  const [typeFilter, setTypeFilter] = useState<'all' | 'meetings' | 'webinars'>('all')
  const [filterOpen, setFilterOpen] = useState(false)
  const filterRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) setFilterOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])
  const [showVideoPreview, setShowVideoPreview] = useState<string | null>(null)
  const [showShareDialog, setShowShareDialog] = useState<string | null>(null)
  const [shareConfig, setShareConfig] = useState({ 
    permission: 'participants_org',
    allowDownloads: true,
    showLinkSettings: false,
    viewerTracking: true,
    expiryEnabled: false,
    expiryValue: '24',
    expiryUnit: 'hours' as 'hours' | 'days'
  })
  const [copiedShareLink, setCopiedShareLink] = useState(false)

  const [recordings, setRecordings] = useState<RecordingCard[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    listRecordings({ limit: 100 })
      .then((res) => {
        if (cancelled) return
        setRecordings(res.recordings.map(toCard))
        setLoadError(null)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        // Shown rather than swallowed: an empty list and a failed load look
        // identical to the user, and only one of them means "you have no
        // recordings".
        setLoadError(err instanceof Error ? err.message : 'Could not load recordings')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const filtered = recordings.filter(r => {
    const matchesSearch = r.title.toLowerCase().includes(search.toLowerCase())
    const matchesType =
      typeFilter === 'all' ||
      (typeFilter === 'meetings' && r.type === 'meeting') ||
      (typeFilter === 'webinars' && r.type === 'webinar')
    return matchesSearch && matchesType
  })

  const buildShareUrl = (recordingId: string) => {
    const params = new URLSearchParams({
      recordingId,
      permission: shareConfig.permission,
      allowDownloads: String(shareConfig.allowDownloads),
      trackViews: String(shareConfig.viewerTracking),
    })

    if (shareConfig.expiryEnabled) {
      params.set('expiresIn', `${shareConfig.expiryValue}${shareConfig.expiryUnit === 'hours' ? 'h' : 'd'}`)
    }

    return `${config.recordingBaseUrl}/recording/public/videoprv?${params.toString()}`
  }

  const handleDownload = async (recordingId: string) => {
    const recording = recordings.find(r => r.id === recordingId)
    if (!recording) return

    if (!shareConfig.allowDownloads) {
      toast.warning({
        title: t("analytics.downloadBlocked"),
        description: t("analytics.downloadBlockedDesc"),
      })
      return
    }

    if (recording.status !== 'ready') {
      toast.warning({
        title: t("analytics.downloadBlocked"),
        description: 'This recording is still being processed.',
      })
      return
    }

    try {
      // The URL from the list load is reused directly. It is a permanent public
      // CDN link, so there is nothing to refresh — the round trip this used to
      // make existed only to outrun a presigned URL's expiry.
      if (!recording.playbackUrl) throw new Error('No playback URL')

      const a = document.createElement('a')
      a.href = recording.playbackUrl
      a.download = `${recording.title}.webm`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)

      toast.success({
        title: t("analytics.downloadStarted"),
        description: `${recording.title} ${t("analytics.beingDownloaded")}`,
      })
    } catch (err) {
      toast.warning({
        title: t("analytics.downloadBlocked"),
        description: err instanceof Error ? err.message : 'Could not start the download',
      })
    }
  }

  /**
   * Delete a recording — the row, the S3 object and the mixer's local copy.
   *
   * Confirmed first, and deliberately not undoable-by-toast: the backend
   * removes the bucket object, so there is nothing to restore afterwards. A
   * one-click delete on a hover control would lose a meeting to a mis-click.
   */
  const handleDelete = async (recordingId: string) => {
    setDeleting(true)
    try {
      await deleteRecording(recordingId)
      // Removed locally rather than refetched: the row is gone server-side, and
      // a refetch would flash the whole list for one removal.
      setRecordings((prev) => prev.filter((r) => r.id !== recordingId))
      setConfirmDelete(null)
      if (showVideoPreview === recordingId) setShowVideoPreview(null)
      toast.success({ title: 'Recording deleted', description: 'The recording and its file were removed.' })
    } catch (err) {
      toast.warning({
        title: 'Could not delete the recording',
        description: err instanceof Error ? err.message : 'Please try again.',
      })
    } finally {
      setDeleting(false)
    }
  }

  const handleCopyShareLink = () => {
    if (!showShareDialog) return

    navigator.clipboard.writeText(buildShareUrl(showShareDialog))
    setCopiedShareLink(true)
    toast.success({ title: t("analytics.copiedToClipboard"), description: t("analytics.copiedToClipboardDesc") })
    setTimeout(() => setCopiedShareLink(false), 1600)
  }

  const handleShare = (platform: string, recordingId: string) => {
    const recording = recordings.find(r => r.id === recordingId)
    if (!recording) return

    if (shareConfig.permission === 'no_share') {
      toast.warning({
        title: t("analytics.sharingDisabled"),
        description: t("analytics.sharingDisabledDesc"),
      })
      return
    }
    
    const shareUrl = buildShareUrl(recordingId)
    
    if (platform === 'copy') {
      navigator.clipboard.writeText(shareUrl)
      toast.success({ title: t("analytics.copiedToClipboard"), description: t("analytics.copiedToClipboardDesc") })
    } else if (platform === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(`Check out this recording: ${recording.title} ${shareUrl}`)}`, '_blank', 'noopener,noreferrer')
    } else if (platform === 'telegram') {
      window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(`Check out this recording: ${recording.title}`)}`, '_blank', 'noopener,noreferrer')
    } else if (platform === 'email') {
      window.open(`mailto:?subject=${encodeURIComponent(`Recording: ${recording.title}`)}&body=${encodeURIComponent(`Check out this recording: ${shareUrl}`)}`)
    }
    
    setShowShareDialog(null)
  }

  const activePermission = PERMISSION_OPTIONS.find(opt => opt.id === shareConfig.permission)
  const shareableLink = showShareDialog ? buildShareUrl(showShareDialog) : ''

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div className="px-4 sm:px-6 py-3  shrink-0">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
          <button onClick={() => navigate('/meetings')} className="text-primary font-semibold hover:text-foreground flex items-center gap-1 transition-colors">
            <ArrowLeft className="w-3 h-3" />{t("common.dashboard")}
          </button>
          <span>/</span><span className="text-foreground">{t("analytics.meetingRecordings")}</span>
        </div>
        <div className="flex flex-col gap-3 mt-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-lg font-bold text-foreground">{t("analytics.meetingRecordings")}</h1>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="flex-1 flex items-center gap-2 bg-muted/50 rounded-xl px-3 py-2 min-w-[120px]">
              <Search className="w-4 h-4 text-muted-foreground shrink-0" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t("analytics.searchRecordingsPlaceholder")}
                className="flex-1 bg-transparent text-sm outline-none text-foreground placeholder:text-muted-foreground"
              />
            </div>
            <div className="relative shrink-0" ref={filterRef}>
              <button
                onClick={() => setFilterOpen(o => !o)}
                className="flex items-center gap-2 border border-border bg-card rounded-lg px-3 py-1.5 text-sm font-medium text-foreground hover:border-primary transition-colors"
              >
                {typeFilter === 'all' ? t('common.all') : typeFilter === 'meetings' ? t('meetings.title') : t('webinar.title')}
                <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
              {filterOpen && (
                <div className="absolute right-0 top-9 z-20 bg-card border border-border rounded-xl shadow-lg min-w-[130px] py-1">
                  {(['all', 'meetings', 'webinars'] as const).map(type => (
                    <button
                      key={type}
                      onClick={() => { setTypeFilter(type); setFilterOpen(false) }}
                      className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                        typeFilter === type
                          ? 'text-primary font-semibold'
                          : 'text-foreground hover:bg-muted'
                      }`}
                    >
                      {type === 'all' ? t('common.all') : type === 'meetings' ? t('meetings.title') : t('webinar.title')}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={() => setView('grid')}
                className={`p-1.5 rounded-lg transition-colors ${view === 'grid' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <svg className="w-4 h-4" viewBox="0 0 16 16" fill="currentColor">
                  <rect x="1" y="1" width="6" height="6" rx="1" />
                  <rect x="9" y="1" width="6" height="6" rx="1" />
                  <rect x="1" y="9" width="6" height="6" rx="1" />
                  <rect x="9" y="9" width="6" height="6" rx="1" />
                </svg>
              </button>
              <button
                onClick={() => setView('list')}
                className={`p-1.5 rounded-lg transition-colors ${view === 'list' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <svg className="w-4 h-4" viewBox="0 0 16 16" fill="currentColor">
                  <rect x="1" y="2" width="14" height="2" rx="1" />
                  <rect x="1" y="7" width="14" height="2" rx="1" />
                  <rect x="1" y="12" width="14" height="2" rx="1" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={`flex-1 overflow-y-auto p-4 sm:p-4 ${filtered.length === 0 || loading || loadError ? 'flex justify-center items-center' : ''}`}>

        {loading
          ? <EmptyState size='lg' image={assetNoRegistrantsCopy} title={t("analytics.loading")} />
          : loadError
            /* Distinct from the empty state on purpose: "you have no recordings"
               and "we could not fetch your recordings" look identical otherwise,
               and only one of them is the user's own situation. */
            ? <EmptyState size='lg' image={assetNoRegistrantsCopy} title={loadError} />
          : filtered.length === 0
          ? <EmptyState size='lg' image={assetNoRegistrantsCopy} title={t("analytics.noRecordings")} />
          : view === 'grid'
            ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {filtered.map((rec, i) => (
                  <motion.div key={rec.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                    className="bg-card border border-border rounded-2xl overflow-hidden group hover:border-primary/40 transition-all">
                    <div className="relative aspect-video bg-muted cursor-pointer" onClick={() => setShowVideoPreview(rec.id)}>
                      {/* Only render an <img> when there is something to load. An
                          <img> with an empty or missing src draws the browser's
                          broken-image glyph, which reads as "this recording is
                          damaged" — worse than the plain surface underneath. */}
                      {rec.thumb
                        ? <img src={rec.thumb} alt={rec.title} className="w-full h-full object-cover"
                               onError={(e) => { e.currentTarget.style.display = 'none' }} />
                        : <div className="w-full h-full flex items-center justify-center">
                            <Play className="w-8 h-8 text-muted-foreground/40" />
                          </div>}
                      
                      {/* Play button - always visible on mobile, hover on desktop */}
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                        <div className="w-11 h-11 rounded-full bg-card/90 flex items-center justify-center shadow-lg">
                          <Play className="w-5 h-5 text-foreground ml-0.5" />
                        </div>
                      </div>
                      
                      {/* Duration badge */}
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] px-2 py-0.5 rounded font-mono">{rec.duration}</div>
                      
                      {/* Download - always visible on mobile, hover on desktop */}
                      <button className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center lg:opacity-0 lg:group-hover:opacity-100 transition-opacity"
                        onClick={(e) => { e.stopPropagation(); handleDownload(rec.id) }}>
                        <Download className="w-4 h-4 text-white" />
                      </button>

                      {/* Delete — offset from Download so a mis-aimed tap on a
                          phone, where both are always visible, cannot destroy a
                          recording instead of saving it. */}
                      <button aria-label="Delete recording"
                        className="absolute top-2 right-11 w-8 h-8 rounded-full bg-black/50 hover:bg-red-600/80 flex items-center justify-center lg:opacity-0 lg:group-hover:opacity-100 transition-opacity"
                        onClick={(e) => { e.stopPropagation(); setConfirmDelete(rec.id) }}>
                        <Trash2 className="w-4 h-4 text-white" />
                      </button>
                      
                      {/* Share - always visible on mobile, hover on desktop */}
                      <button className="absolute top-2 left-2 w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center lg:opacity-0 lg:group-hover:opacity-100 transition-opacity"
                        onClick={(e) => { e.stopPropagation(); setShowShareDialog(rec.id) }}>
                        <Share2 className="w-4 h-4 text-white" />
                      </button>
                      
                      {/* Type chip */}
                      <div className="absolute bottom-2 left-10  transform -translate-x-1/2">
                        <Badge variant={rec.type === 'webinar' ? 'secondary' : 'secondary'} className="text-[10px] px-2 py-0.5 bg-black/50 hover:bg-black/70">
                          {rec.type === 'webinar' ? t("webinar.title") : t("meetings.title")}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="p-3">
                      <p className="text-sm font-semibold text-foreground truncate mb-2">{rec.title}</p>
                      
                      {/* Host info */}
                      <div className="flex items-center gap-2 mb-2">
                        <img src={rec.host.avatar} alt={rec.host.name} className="w-5 h-5 rounded-full" />
                        <span className="text-xs text-muted-foreground truncate">{rec.host.name}</span>
                      </div>
                      
                      {/* Recorded time */}
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Calendar className="w-3 h-3" />
                        <span>{rec.recordedAt}</span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )
            : (
              <div className="space-y-2">
                {filtered.map((rec, i) => (
                  <motion.div key={rec.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                    className="bg-card border border-border rounded-2xl px-4 py-3 flex items-center gap-4 hover:border-primary/40 transition-all group cursor-pointer"
                    onClick={() => setShowVideoPreview(rec.id)}>
                    
                    <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center shrink-0 relative overflow-hidden">
                      {rec.thumb
                        ? <img src={rec.thumb} alt={rec.title} className="w-full h-full object-cover"
                               onError={(e) => { e.currentTarget.style.display = 'none' }} />
                        : <Play className="w-4 h-4 text-muted-foreground/50" />}
                      <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <Play className="w-4 h-4 text-white" />
                      </div>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-semibold text-foreground truncate">{rec.title}</p>
                        <Badge variant={rec.type === 'webinar' ? 'default' : 'secondary'} className="text-[10px] px-2 py-0.5 shrink-0">
                          {rec.type === 'webinar' ? t("webinar.title") : t("meetings.title")}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <img src={rec.host.avatar} alt={rec.host.name} className="w-3 h-3 rounded-full" />
                          <span>{rec.host.name}</span>
                        </div>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {rec.recordedAt}
                        </span>
                        <span className="flex items-center gap-1 hidden sm:flex">
                          <Clock className="w-3 h-3" />
                          {rec.duration}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex gap-1.5 shrink-0">
                      <button className="p-2 rounded-lg hover:bg-muted transition-colors" 
                        onClick={(e) => { e.stopPropagation(); handleDownload(rec.id) }}>
                        <Download className="w-4 h-4 text-muted-foreground" />
                      </button>
                      <button aria-label="Delete recording"
                        className="w-8 h-8 rounded-lg hover:bg-red-500/10 flex items-center justify-center transition-colors"
                        onClick={(e) => { e.stopPropagation(); setConfirmDelete(rec.id) }}>
                        <Trash2 className="w-4 h-4 text-muted-foreground hover:text-red-500" />
                      </button>
                      <button className="p-2 rounded-lg hover:bg-muted transition-colors"
                        onClick={(e) => { e.stopPropagation(); setShowShareDialog(rec.id) }}>
                        <Share2 className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )
        }
      </div>

      {/* Delete confirmation — a recording cannot be recovered once the bucket
          object is gone, so this asks before it destroys anything. */}
      <Dialog open={!!confirmDelete} onOpenChange={() => !deleting && setConfirmDelete(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete this recording?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            The recording file is deleted from storage as well as from this list.
            This cannot be undone.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" disabled={deleting} onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={deleting}
              onClick={() => confirmDelete && handleDelete(confirmDelete)}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Video Preview Dialog */}
      <Dialog open={!!showVideoPreview} onOpenChange={() => setShowVideoPreview(null)} size="lg">
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{t("analytics.recordingPreview")}</DialogTitle>
          </DialogHeader>
          <div className="aspect-video bg-black rounded-lg overflow-hidden">
            <video 
              className="w-full h-full" 
              controls 
              autoPlay
              src={recordings.find(r => r.id === showVideoPreview)?.playbackUrl ?? undefined}
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog
        open={!!showShareDialog}
        onOpenChange={(open) => {
          if (!open) {
            setShowShareDialog(null)
            setCopiedShareLink(false)
          }
        }}
        size="sm"
      >
        <DialogContent className="w-[calc(100%-2rem)] max-w-xl overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">
              {t("analytics.shareRecording")} - {showShareDialog ? recordings.find(r => r.id === showShareDialog)?.title : t("analytics.recording")}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 max-w-full overflow-x-hidden">
            {/* Link Permission */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-foreground">{t("analytics.linkPermission")}</label>
              <Select
                value={shareConfig.permission}
                onValueChange={(value) => setShareConfig(prev => ({ ...prev, permission: value }))}
              >
                <SelectTrigger className="w-full mt-2 max-w-full">
                  <SelectValue>
                    {PERMISSION_OPTIONS.find(opt => opt.id === shareConfig.permission)?.title}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="w-[--radix-select-trigger-width] max-w-[calc(100vw-4rem)]">
                  {PERMISSION_OPTIONS.map((option) => (
                    <SelectItem key={option.id} value={option.id} className="p-3">
                      <div className="flex items-start gap-3">
                        <option.icon className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground">{option.title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{option.description}</p>
                        </div>
                        {shareConfig.permission === option.id && (
                          <CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Shareable Link */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-foreground">{t("analytics.shareableLink")}</label>
              <div className="flex items-center mt-2 gap-2 p-2 bg-muted/30 rounded-lg border max-w-full overflow-hidden">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground truncate font-mono">
                    {shareableLink || `${config.recordingBaseUrl}/recording/public/videoprv?recordingId=...`}
                  </p>
                </div>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="shrink-0 h-8 w-8 p-0"
                  onClick={handleCopyShareLink}
                >
                  {copiedShareLink ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-muted/60">
                  {activePermission?.title}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-muted/60">
                  {shareConfig.allowDownloads ? t("analytics.downloadsAllowed") : t("analytics.downloadsBlocked")}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-muted/60">
                  {shareConfig.expiryEnabled ? `${t("common.expiresIn")} ${shareConfig.expiryValue} ${shareConfig.expiryUnit}` : t("common.noExpiry")}
                </span>
              </div>
            </div>

            {/* Link Settings - Collapsible */}
            <div className="space-y-3">
              <button
                onClick={() => setShareConfig(prev => ({ ...prev, showLinkSettings: !prev.showLinkSettings }))}
                className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
              >
                {t("analytics.linkSettings")}
                <ChevronDown className={`w-4 h-4 transition-transform ${shareConfig.showLinkSettings ? 'rotate-180' : ''}`} />
              </button>
              
              {shareConfig.showLinkSettings && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-4 pl-4 border-l-2 border-border"
                >
                  {/* Viewer tracking */}
                  <div className="flex items-start gap-3">
                    <Eye className="w-4 h-4 text-muted-foreground mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-medium text-foreground">{t("analytics.viewerTracking")}</p>
                        <Switch
                          checked={shareConfig.viewerTracking}
                          onCheckedChange={(checked) => setShareConfig(prev => ({ ...prev, viewerTracking: checked }))}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">{t("analytics.viewerTrackingDesc")}</p>
                    </div>
                  </div>

                  {/* Access expiry */}
                  <div className="flex items-start gap-3">
                    <Settings className="w-4 h-4 text-muted-foreground mt-1" />
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-medium text-foreground">{t("analytics.accessExpiry")}</p>
                        <Switch
                          checked={shareConfig.expiryEnabled}
                          onCheckedChange={(checked) => setShareConfig(prev => ({ ...prev, expiryEnabled: checked }))}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">{t("analytics.setExpiry")}</p>

                      {shareConfig.expiryEnabled && (
                        <div className="flex items-center gap-2 pt-1">
                          <Select
                            value={shareConfig.expiryValue}
                            onValueChange={(value) => setShareConfig(prev => ({ ...prev, expiryValue: value }))}
                          >
                            <SelectTrigger className="w-24 h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">1</SelectItem>
                              <SelectItem value="6">6</SelectItem>
                              <SelectItem value="12">12</SelectItem>
                              <SelectItem value="24">24</SelectItem>
                              <SelectItem value="48">48</SelectItem>
                              <SelectItem value="72">72</SelectItem>
                            </SelectContent>
                          </Select>

                          <Select
                            value={shareConfig.expiryUnit}
                            onValueChange={(value) => setShareConfig(prev => ({ ...prev, expiryUnit: value as 'hours' | 'days' }))}
                          >
                            <SelectTrigger className="w-28 h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="hours">Hours</SelectItem>
                              <SelectItem value="days">Days</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Allow Downloads - moved to end */}
                  <div className="flex items-start gap-3">
                    <Download className="w-4 h-4 text-muted-foreground mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-medium text-foreground">{t("analytics.allowDownloads")}</p>
                        <Switch
                          checked={shareConfig.allowDownloads}
                          onCheckedChange={(checked) => setShareConfig(prev => ({ ...prev, allowDownloads: checked }))}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">{t("analytics.allowDownloadsDesc")}</p>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Share Via Platforms */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-foreground">{t("analytics.shareVia")}</label>
              <div className="flex mt-2  items-center gap-3">
                {SHARE_PLATFORMS.map((platform) => (
                  <button
                    key={platform.id}
                    onClick={() => handleShare(platform.id, showShareDialog!)}
                    className={`w-10 h-10 rounded-full ${platform.color} flex items-center justify-center text-white hover:opacity-80 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed`}
                    title={platform.name}
                    disabled={shareConfig.permission === 'no_share'}
                  >
                    {typeof platform.icon === 'string' ? (
                      <img src={platform.icon} alt={platform.name} className="w-10 h-10" />
                    ) : (
                      <platform.icon className="w-5 h-5" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
